import { useContext, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import isObject from 'lodash/isObject'

import { PageContext } from '@pages/DiagramsCreateOrEdit'
import { ColumnType, seriesTypeString } from '@pages/DiagramsCreateOrEdit/types'

import { getDataFromState, getStateFromData } from '@components/CommandParameters/helpers'

import { useFetchObjectByCodeQuery } from '@redux/api'
import { useCreateOrUpdateBarChartMutation } from '@redux/api/barChart.api'

import { usePrompt } from '@hooks'
import { AutocompleteOption } from '@types'

import {
  columnBindTypes,
  columnOperations,
  ColumnRequestType,
  OBJECT_TYPE,
  OPERATION_TYPE,
} from '../../../../../types/api/barChart.api'
import { getDefaultValue } from '../helpers'

export const useHandlers = () => {
  const { series, updateDiagramData, onCloseModal, diagramId, editColumnData } =
    useContext(PageContext)
  const [isOpenScriptValueDialog, setIsOpenScriptValueDialog] = useState(false)
  const [loading, setLoading] = useState(false)
  const defaultValues: ColumnType = getDefaultValue(series)
  const methods = useForm<ColumnType>({ defaultValues })
  const {
    handleSubmit,
    reset,
    formState: { isDirty, dirtyFields },
    setValue,
    watch,
  } = methods
  const [currentNameScriptDialog, setCurrentNameScriptDialog] = useState()

  const watchObject = watch('object')
  const watchCommand = watch('command')

  const [requestBarChart] = useCreateOrUpdateBarChartMutation()

  const { data: objectData } = useFetchObjectByCodeQuery(watchObject?.id, {
    skip: !watchObject,
  })

  const objectFields = useMemo<AutocompleteOption[]>(
    () =>
      objectData?.fields.map(field => ({
        label: field.name,
        id: field.id,
      })) || [],
    [objectData?.fields]
  )

  const scriptHint = useMemo(
    () =>
      currentNameScriptDialog === 'jsCreateValue'
        ? `Input: srcObj: { \ndata: {}, \nseries: ${seriesTypeString}, \nindex: number }\nOutput: number`
        : `Input: srcObj: { ${objectFields
            ?.map(field => field.label)
            .join(', ')} }\nOutput: number`,
    [currentNameScriptDialog, objectFields]
  )

  const commandOptions: AutocompleteOption[] =
    objectData?.commands
      ?.filter(command => command.httpMethod === 'GET')
      .map(command => ({
        label: command.code,
        id: command.code,
      })) || []

  usePrompt({ when: isDirty })

  useEffect(() => {
    if (editColumnData) {
      reset({
        ...defaultValues,
        ...editColumnData,
        object: {
          label: editColumnData.objectTitle,
          id: editColumnData.objectCode,
        },
        operation: columnOperations.find(({ id }) => id === editColumnData.operation),
        fields: series.map(singleSeries => {
          const savedFieldData = editColumnData.fields.find(
            field => field.seriesId === singleSeries.id
          )

          return {
            ...savedFieldData,
            seriesId: singleSeries.id,
            bindType:
              columnBindTypes.find(({ id }) => id === savedFieldData?.bindType) ||
              columnBindTypes[0],
            title: singleSeries.title,
          }
        }),
        command: {
          id: editColumnData.command.name,
          label: editColumnData.command.name,
        },
        commandParameters: getStateFromData(editColumnData.command.parameters),
        commandBody: getStateFromData(editColumnData.command.body),
      })
    }
  }, [])

  const toggleOpenScriptValueDialog = (value: boolean) => {
    setIsOpenScriptValueDialog(value)
  }

  const onChangeColor = (color: string) => {
    setValue('color', color, { shouldDirty: true })
  }

  const handleSaveColumn = handleSubmit(data => {
    const requestArray = []
    setLoading(true)
    if (data.id) {
      const columnParameter = editColumnData.parameters?.find(param => param.paramKey === 'COLUMN')
      const updateColumn: ColumnRequestType = {
        operationType: OPERATION_TYPE.UPDATE,
        type: OBJECT_TYPE.COLUMN,
        data: {
          id: data.id,
          code: data.code,
          title: data.title,
          operation: data.operation?.id,
          diagramId,
          prefilter: data.prefilter,
          objectCode: data.object?.id,
          objectTitle: data.object?.label,
          order: 0,
          commandForEverySeries: data.commandForEverySeries,
          jsForAllFields: data.jsForAllFields,
          jsCreateValue: data.jsCreateValue,
          command: {
            ...objectData?.commands.find(command => command.code === data.command.id),
            name: data.command.id,
            resource: 'OBJECT',
            body: getDataFromState(data.commandBody),
            objectCode: data.object?.id,
            parameters: getDataFromState(data.commandParameters),
          },
          fields: data.fields.map(field => ({
            value: isObject(field.value) ? field.value?.label : field.value,
            bindType: field.bindType.id,
            seriesId: field.seriesId,
          })),
          color: data.color,
          /*parameters: [
            {
              ...columnParameter,
              param: {
                properties: [
                  {
                    code: 'column_background_color_ref',
                    key: 'backgroundColor',
                    value: data.parameters.color,
                  },
                ],
              },
              paramKey: 'HEADER',
            },
          ],*/
        },
      }
      requestArray.push(updateColumn)
      requestBarChart(requestArray)
        .then(resp => {
          if (resp.data) {
            updateDiagramData(resp.data)
            onCloseModal(false)
          }
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      const createColumn: ColumnRequestType = {
        operationType: OPERATION_TYPE.CREATE,
        type: OBJECT_TYPE.COLUMN,
        data: {
          code: data.code,
          title: data.title,
          operation: data.operation?.id,
          diagramId,
          prefilter: data.prefilter,
          objectCode: data.object?.id,
          objectTitle: data.object?.label,
          order: 0,
          commandForEverySeries: data.commandForEverySeries,
          jsForAllFields: data.jsForAllFields,
          jsCreateValue: data.jsCreateValue,
          command: {
            ...objectData?.commands.find(command => command.code === data.command.id),
            name: data.command.id,
            resource: 'OBJECT',
            body: getDataFromState(data.commandBody),
            objectCode: data.object?.id,
            parameters: getDataFromState(data.commandParameters),
          },
          fields: data.fields.map(field => ({
            value: field.value?.label,
            bindType: field.bindType.id,
            seriesId: field.seriesId,
          })),
          color: data.color,
          /*parameters: [
            {
              param: {
                properties: [{ key: 'color', value: data.parameters.color }],
              },
              paramKey: 'COLUMN',
            },
          ],*/
        },
      }
      requestBarChart([createColumn])
        .then(resp => {
          if (resp.data) {
            updateDiagramData(resp.data)
            onCloseModal(false)
          }
        })
        .finally(() => {
          setLoading(false)
        })
    }
  })

  const handleChangeObject = () => {
    setValue('command', null)
    setValue('commandParameters', [])
    setValue('commandBody', [])
  }

  return {
    data: {
      objectFields,
      commandOptions,
    },
    state: {
      isDirty,
      isOpenScriptValueDialog,
      currentNameScriptDialog,
      scriptHint,
      saveLoading: loading,
    },
    handlers: {
      onChangeColor,
      handleSaveColumn,
      setCurrentNameScriptDialog,
      toggleOpenScriptValueDialog,
      handleChangeObject,
    },
    methods,
  }
}
